import {
  sanitizeCoPilotExportAttachmentFilename,
  sanitizeCoPilotExportRequestSlug,
} from '@terra/shared-chat-trace';
import type { CoPilotTraceExportStatus } from '../../../utils/co-pilot-trace-export';
import { createApiHeaders, getBaseUrlOverride } from '../api-core';

export const COPILOT_SESSION_EXPORT_FORMAT = {
  MARKDOWN: 'markdown',
  CSV: 'csv',
  HAR: 'har',
} as const;

export type CoPilotSessionExportFormat =
  (typeof COPILOT_SESSION_EXPORT_FORMAT)[keyof typeof COPILOT_SESSION_EXPORT_FORMAT];

export type CoPilotSessionExportFile = {
  blob: Blob;
  filename: string;
};

const EXPORT_PATH_BY_FORMAT: Record<CoPilotSessionExportFormat, string> = {
  markdown: 'markdown',
  csv: 'csv',
  har: 'har',
};

function readFilenameFromContentDisposition(
  contentDisposition: string | null,
): string | undefined {
  if (!contentDisposition) {
    return undefined;
  }

  const filenameMatch = /filename="([^"]+)"/i.exec(contentDisposition);

  return filenameMatch?.[1];
}

function isValidIanaTimeZone(timeZone: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone });

    return true;
  } catch {
    return false;
  }
}

function resolveClientTimeZone(): string {
  let resolved: string | undefined;

  try {
    resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }

  const trimmed = resolved?.trim();

  if (!trimmed || !isValidIanaTimeZone(trimmed)) {
    return 'UTC';
  }

  return trimmed;
}

function buildFallbackFilename({
  chatTitle,
  requestFilename,
  format,
}: {
  chatTitle?: string;
  requestFilename?: string;
  format: CoPilotSessionExportFormat;
}): string {
  const extensionByFormat: Record<CoPilotSessionExportFormat, string> = {
    markdown: 'md',
    csv: 'csv',
    har: 'csv',
  };
  const label = requestFilename?.trim() || chatTitle?.trim();
  const slug = label
    ? sanitizeCoPilotExportRequestSlug(label)
    : 'co_pilot_chat';

  if (format === 'har') {
    return `${slug}_har.csv`;
  }

  return `${slug}.${extensionByFormat[format]}`;
}

async function readExportErrorMessage({
  response,
  format,
}: {
  response: Response;
  format: CoPilotSessionExportFormat;
}): Promise<string> {
  const fallback = `Co-pilot ${format} export failed with status ${response.status}`;

  try {
    const body = (await response.json()) as {
      message?: string | string[];
    };
    const message = Array.isArray(body.message)
      ? body.message[0]
      : body.message;

    return message?.trim() || fallback;
  } catch {
    return fallback;
  }
}

export const portalCoPilotSessionExportApi = {
  fetchCoPilotSessionExport: async ({
    threadId,
    applicationId,
    format,
    status = 'completed',
    chatTitle,
    requestFilename,
    attachmentFilename,
    csvObjectKinds,
    csvSources,
    csvKind,
    markdownKind,
  }: {
    threadId: string;
    applicationId: string;
    format: CoPilotSessionExportFormat;
    status?: CoPilotTraceExportStatus;
    chatTitle?: string;
    requestFilename?: string;
    attachmentFilename?: string;
    csvObjectKinds?: string[];
    csvSources?: string[];
    csvKind?: 'transcript' | 'objects' | 'http_exchanges';
    markdownKind?: 'transcript' | 'objects' | 'session' | 'http_exchanges';
  }): Promise<CoPilotSessionExportFile> => {
    const headers = await createApiHeaders();
    const query = new URLSearchParams();

    if (format === 'markdown' || format === 'csv') {
      query.set('timeZone', resolveClientTimeZone());
    }

    if (status !== 'completed') {
      query.set('status', status);
    }

    if (requestFilename?.trim()) {
      query.set('requestFilename', requestFilename.trim());
    } else if (chatTitle?.trim()) {
      query.set('title', chatTitle.trim());
    }

    if (attachmentFilename?.trim()) {
      query.set('attachmentFilename', attachmentFilename.trim());
    }

    if (format === 'csv' && csvKind) {
      query.set('csvKind', csvKind);
    }

    if (
      format === 'markdown' &&
      markdownKind &&
      markdownKind !== 'transcript'
    ) {
      query.set('markdownKind', markdownKind);
    }

    if (
      (format === 'csv' || format === 'markdown') &&
      csvObjectKinds &&
      csvObjectKinds.length > 0
    ) {
      query.set('objectKinds', csvObjectKinds.join(','));
    }

    if (
      (format === 'csv' || format === 'markdown') &&
      csvSources &&
      csvSources.length > 0
    ) {
      query.set('sources', csvSources.join(','));
    }

    const queryString = query.toString();
    const exportSegment = EXPORT_PATH_BY_FORMAT[format];
    const response = await fetch(
      `${getBaseUrlOverride('ai')}/chat-history/thread/${applicationId}/${threadId}/export/${exportSegment}${queryString ? `?${queryString}` : ''}`,
      {
        method: 'GET',
        credentials: 'include',
        headers,
      },
    );

    if (response.status === 404) {
      throw new Error('Chat history not found');
    }

    if (!response.ok) {
      throw new Error(await readExportErrorMessage({ response, format }));
    }

    const blob = await response.blob();
    const filename = sanitizeCoPilotExportAttachmentFilename(
      readFilenameFromContentDisposition(
        response.headers.get('Content-Disposition'),
      ) ??
        attachmentFilename?.trim() ??
        buildFallbackFilename({ chatTitle, requestFilename, format }),
    );

    return { blob, filename };
  },
};
